import { z } from 'zod';
import { listSortSchema, listPreferencesSchema, type ListView } from '@/components/listPreferences';
import { useStoredPreference } from '@/hooks/useStoredPreference';

interface LocalListOptions {
    key: string;
    search: string;
    onSearch: (search: string) => void;
    initialSort?: ListView['sort'];
    filters?: Record<string, string>;
    context?: string;
    defaultFilters?: Record<string, string>;
    defaultContext?: string;
    onRestore?: (filters: Record<string, string>, context: string) => void;
}

export const useLocalList = ({ key, search, onSearch, initialSort = { key: 'name', order: 'asc' }, filters = {}, context = '', defaultFilters = {}, defaultContext = '', onRestore }: LocalListOptions) => {
    const [preferences, setPreferences] = useStoredPreference(`${key}:table`, listPreferencesSchema, { density: 'compact', hidden: [] });
    const [sort, setSort] = useStoredPreference(`${key}:sort`, listSortSchema, initialSort);
    const [pageSize, setPageSize] = useStoredPreference(`${key}:size`, z.number().int().min(1).max(100), 20);
    const query = JSON.stringify({ search, filters, context });
    const [cursor, setCursor] = useStoredPreference(`${key}:cursor`, z.object({ query: z.string(), page: z.number().int().min(1) }), { query, page: 1 }, true);
    const view: ListView = { search, filters, context, preferences, sort, pageSize };
    const defaultView: ListView = { search: '', filters: defaultFilters, context: defaultContext, preferences: { density: 'compact', hidden: [] }, sort: initialSort, pageSize: 20 };
    const apply = (saved: ListView): void => {
        onSearch(saved.search); setSort(saved.sort); setPreferences(saved.preferences); setPageSize(saved.pageSize);
        onRestore?.(saved.filters, saved.context); setCursor({ query: '', page: 1 });
    };
    const pagination = (total: number) => ({ total, pageSize, page: cursor.query === query ? cursor.page : 1,
        onPage: (page: number): void => setCursor({ query, page }),
        onPageSize: (size: number): void => { setPageSize(size); setCursor({ query, page: 1 }); },
    });
    return { preferences, setPreferences, sort, setSort, view, defaultView, apply, pagination };
};
